import React, { useState, useEffect } from 'react';
import { FileText, Calendar, MapPin, Plus, Download, Search, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getCurrentUser } from '@/data/users';

export default function IncidentReportsPage() {
  const currentUser = getCurrentUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    setTimeout(() => {
      setIncidents([
        { id: 'INC-001', title: 'Physical Altercation Between Students', category: 'safety', location: 'School Playground', reported_by: 'Mr. John Teacher', date: '2024-12-10', time: '10:30 AM', status: 'investigating', severity: 'high' },
        { id: 'INC-002', title: 'Property Damage - Classroom Window', category: 'property', location: 'Room 105', reported_by: 'Mrs. Sarah Admin', date: '2024-12-09', time: '2:15 PM', status: 'resolved', severity: 'medium' },
        { id: 'INC-003', title: 'Unauthorized Entry to Staff Room', category: 'security', location: 'Staff Room', reported_by: 'Mr. Peter Security', date: '2024-12-08', time: '4:00 PM', status: 'closed', severity: 'low' },
        { id: 'INC-004', title: 'Medical Emergency - Student Fainted', category: 'medical', location: 'Science Lab', reported_by: 'Ms. Lisa Science', date: '2024-12-07', time: '11:00 AM', status: 'resolved', severity: 'high' }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getCategoryBadge = (category) => {
    const styles = { safety: 'bg-red-100 text-red-800', property: 'bg-yellow-100 text-yellow-800', security: 'bg-purple-100 text-purple-800', medical: 'bg-blue-100 text-blue-800', behavioral: 'bg-orange-100 text-orange-800' };
    return <Badge className={styles[category] || 'bg-gray-100'}>{category}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = { reported: 'bg-gray-100', investigating: 'bg-blue-100 text-blue-800', resolved: 'bg-green-100 text-green-800', closed: 'bg-slate-100 text-slate-800' };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getSeverityIndicator = (severity) => {
    const colors = { high: 'bg-red-500', medium: 'bg-yellow-500', low: 'bg-green-500' };
    return <div className={`w-3 h-3 rounded-full ${colors[severity] || 'bg-gray-500'}`} title={severity} />;
  };

  const filteredIncidents = incidents.filter(incident => {
    const matchesSearch = incident.title.toLowerCase().includes(searchTerm.toLowerCase()) || incident.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || incident.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = {
    total: incidents.length,
    active: incidents.filter(i => i.status === 'investigating' || i.status === 'reported').length,
    resolved: incidents.filter(i => i.status === 'resolved' || i.status === 'closed').length,
    highPriority: incidents.filter(i => i.severity === 'high').length
  };

  return (
    <div className="space-y-6" data-testid="incident-reports-page">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <FileText className="h-8 w-8 text-orange-500" />
            Incident Reports
          </h1>
          <p className="text-muted-foreground mt-1">Document and track school incidents</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline"><Download className="h-4 w-4 mr-2" />Export</Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild><Button data-testid="new-report-btn"><Plus className="h-4 w-4 mr-2" />New Report</Button></DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader><DialogTitle>Create Incident Report</DialogTitle><DialogDescription>Document a new incident for investigation.</DialogDescription></DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2"><Label>Incident Title</Label><Input placeholder="Brief description" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2"><Label>Category</Label>
                    <Select><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="safety">Safety</SelectItem>
                        <SelectItem value="property">Property</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="medical">Medical</SelectItem>
                        <SelectItem value="behavioral">Behavioral</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2"><Label>Severity</Label>
                    <Select><SelectTrigger><SelectValue placeholder="Select severity" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2"><Label>Location</Label><Input placeholder="Where did it occur?" /></div>
                  <div className="grid gap-2"><Label>Time</Label><Input type="time" /></div>
                </div>
                <div className="grid gap-2"><Label>Involved Parties</Label><Input placeholder="Names of people involved" /></div>
                <div className="grid gap-2"><Label>Description</Label><Textarea placeholder="Detailed description..." rows={4} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button onClick={() => setIsAddDialogOpen(false)}>Submit Report</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Total Reports</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{stats.total}</div></CardContent></Card>
        <Card className="border-l-4 border-l-orange-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Active Cases</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-orange-600">{stats.active}</div></CardContent></Card>
        <Card className="border-l-4 border-l-green-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Resolved</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-green-600">{stats.resolved}</div></CardContent></Card>
        <Card className="border-l-4 border-l-red-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">High Priority</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-red-600">{stats.highPriority}</div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by title or incident ID..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-input" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter"><Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="safety">Safety</SelectItem>
                <SelectItem value="property">Property</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="medical">Medical</SelectItem>
                <SelectItem value="behavioral">Behavioral</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Incident Reports</CardTitle><CardDescription>All documented incidents and their status</CardDescription></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
          ) : filteredIncidents.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground"><FileText className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No incident reports found</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead className="w-[50px]"></TableHead><TableHead>ID</TableHead><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Location</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredIncidents.map((incident) => (
                  <TableRow key={incident.id} data-testid={`incident-${incident.id}`}>
                    <TableCell>{getSeverityIndicator(incident.severity)}</TableCell>
                    <TableCell className="font-mono text-sm">{incident.id}</TableCell>
                    <TableCell className="font-medium max-w-[200px] truncate">{incident.title}</TableCell>
                    <TableCell>{getCategoryBadge(incident.category)}</TableCell>
                    <TableCell><div className="flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" />{incident.location}</div></TableCell>
                    <TableCell><div className="flex items-center gap-1"><Calendar className="h-3 w-3 text-muted-foreground" />{incident.date}</div></TableCell>
                    <TableCell>{getStatusBadge(incident.status)}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm">View Details</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
